import type { ExpandedState } from "@tanstack/react-table";
import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { cn } from "@/lib/utils.ts";
import { GICS_LEVEL_CONFIG } from "../config.ts";
import type {
  FlattenedNode,
  GicsLevel,
  GicsTreeNode,
  SelectedIndustry,
} from "../types.ts";
import { findPathToNode, flattenTree, searchTree } from "../utils.ts";
import { GicsHierarchyTable } from "./GicsHierarchyTable.tsx";
import { GicsLevelBadge } from "./GicsLevelBadge.tsx";

// --- File-local components ---

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

function SearchInput({ value, onChange, placeholder }: SearchInputProps) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn("pl-9", value && "pr-9")}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

interface SearchResultItemProps {
  result: FlattenedNode;
  isSelected: boolean;
  onSelect: () => void;
}

function SearchResultItem({
  result,
  isSelected,
  onSelect,
}: SearchResultItemProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full px-4 py-3 text-left hover:bg-muted transition-colors",
        isSelected && "bg-primary/10",
      )}
    >
      <div className="flex items-center gap-2">
        <GicsLevelBadge level={result.level} />
        <span className="font-medium">{result.name}</span>
        <span className="text-xs text-muted-foreground font-mono">
          {result.code}
        </span>
      </div>
      {result.breadcrumb && (
        <div className="mt-1 text-xs text-muted-foreground truncate">
          {result.breadcrumb}
        </div>
      )}
    </button>
  );
}

interface SearchResultsListProps {
  results: FlattenedNode[];
  currentId: string | undefined;
  onSelect: (result: FlattenedNode) => void;
}

function SearchResultsList({
  results,
  currentId,
  onSelect,
}: SearchResultsListProps) {
  if (results.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No industries found
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {results.map((result) => (
        <SearchResultItem
          key={result.id}
          result={result}
          isSelected={currentId === result.id}
          onSelect={() => onSelect(result)}
        />
      ))}
    </div>
  );
}

interface TreeBrowserPanelProps {
  treeData: GicsTreeNode[];
  expanded: ExpandedState;
  onExpandedChange: (expanded: ExpandedState) => void;
  onRowClick: (node: GicsTreeNode) => void;
  selectedId: string | undefined;
  selectableLevels: GicsLevel[];
}

function TreeBrowserPanel({
  treeData,
  expanded,
  onExpandedChange,
  onRowClick,
  selectedId,
  selectableLevels,
}: TreeBrowserPanelProps) {
  return (
    <GicsHierarchyTable
      data={treeData}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onRowClick={onRowClick}
      selectedId={selectedId}
      selectableLevels={selectableLevels}
      className="h-full"
    />
  );
}

// --- Main component ---

export interface IndustryBrowserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (industry: SelectedIndustry) => void;
  currentValue: SelectedIndustry | null;
  treeData: GicsTreeNode[];
  selectableLevels?: GicsLevel[];
  title?: string;
  description?: string;
}

export function IndustryBrowserDialog({
  open,
  onOpenChange,
  onSelect,
  currentValue,
  treeData,
  selectableLevels = [1, 2, 3, 4],
  title = "Browse Industries",
  description = "Navigate through the GICS hierarchy and select an industry",
}: IndustryBrowserDialogProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [searchQuery, setSearchQuery] = useState("");
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const flattenedData = useMemo(() => flattenTree(treeData), [treeData]);

  useEffect(() => {
    if (open && currentValue && treeData.length > 0) {
      const path = findPathToNode(treeData, currentValue.id);
      if (path && path.length > 0) {
        const expandedState: ExpandedState = {};
        for (const id of path) {
          expandedState[id] = true;
        }
        setExpanded(expandedState);

        setTimeout(() => {
          const row = tableContainerRef.current?.querySelector(
            `[data-row-id="${currentValue.id}"]`,
          );
          row?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    }
  }, [open, currentValue, treeData]);

  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const results = searchTree(flattenedData, searchQuery);
    if (selectableLevels.length < 4) {
      return results.filter((r) => selectableLevels.includes(r.level));
    }
    return results;
  }, [searchQuery, flattenedData, selectableLevels]);

  const handleRowClick = (node: GicsTreeNode) => {
    onSelect({ id: node.id, name: node.name, level: node.level });
    onOpenChange(false);
  };

  const handleSearchResultSelect = (result: FlattenedNode) => {
    onSelect({ id: result.id, name: result.name, level: result.level });
    onOpenChange(false);
  };

  const showSearchResults = searchQuery.length >= 2;
  const expandedCount = Object.values(expanded).filter(Boolean).length;
  const selectableLevelLabels = selectableLevels
    .map((l) => GICS_LEVEL_CONFIG[l].label)
    .join(", ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={`Search ${selectableLevelLabels.toLowerCase()}...`}
        />

        {showSearchResults ? (
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden border rounded-md">
            <SearchResultsList
              results={searchResults}
              currentId={currentValue?.id}
              onSelect={handleSearchResultSelect}
            />
          </div>
        ) : (
          <div
            ref={tableContainerRef}
            className="flex-1 min-h-0 overflow-auto border rounded-md"
          >
            <TreeBrowserPanel
              treeData={treeData}
              expanded={expanded}
              onExpandedChange={setExpanded}
              onRowClick={handleRowClick}
              selectedId={currentValue?.id}
              selectableLevels={selectableLevels}
            />
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
          <span>
            {showSearchResults
              ? `${searchResults.length} search results`
              : `${treeData.length} sectors, ${expandedCount} expanded`}
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
